/* eslint-disable id-length */
import type { JSX, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { parseHex } from 'culori/fn';
import { useColorPickerState } from './ColorPickerContext';
import { Input } from '../Input';
import { convertToHsv } from './color-conversion-utils';

const SIX_DIGIT_HEX_CODE_LENGTH = 7; // Including the leading '#'

const isValidHexChar = (char: string): boolean => {
  return /^[0-9a-fA-F]$/.test(char);
};

const normalizeHexInput = (input: string): string => {
  // Remove any leading or trailing whitespace
  let cleaned = input.trim();

  // Ensure it starts with #
  if (!cleaned.startsWith('#')) {
    cleaned = `#${cleaned}`;
  }

  // Remove any extra # characters after the first one
  cleaned = `#${cleaned.slice(1).replace(/#/g, '')}`;

  // Limit to 7 characters total (#RRGGBB)
  return cleaned.slice(0, SIX_DIGIT_HEX_CODE_LENGTH).toLocaleLowerCase();
};

// Unlike a regular HTML input, autoFocus here defaults to true because we
// expect that HexColorInput will usually be used inside
// ColorPickerPopoverContent, and we want it to get focused immediately
// when the popover opens to faciliate quick & easy copy/pasting.
export type HexColorInputProps = {
  /**
   * Whether the input should recieve focus automatically on mount.
   */
  autoFocus?: boolean;
};

/**
 * A text input for entering hex color codes directly. Validates input to
 * allow only hex characters, normalizes pasted values, and supports arrow-key
 * nudging of saturation. Auto-focuses by default for quick paste workflows.
 */
export const HexColorInput = ({ autoFocus = true }: HexColorInputProps): JSX.Element => {
  const { valueAsHex, nonDerivedValueAsHsv, onChangeNonDerivedValueAsHsv } = useColorPickerState();
  const inputRef = useRef<HTMLInputElement>(null);
  const [textInputValue, setTextInputValue] = useState(valueAsHex);

  const onChangeInputValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = (event.target.value || '#').trim().toLocaleLowerCase();
      setTextInputValue(newValue);

      const isValidHexValue = Boolean(parseHex(newValue));

      if (!isValidHexValue) {
        return;
      }

      if (newValue.length < SIX_DIGIT_HEX_CODE_LENGTH) {
        // We don't want to interrupt typing and replace a sub-six digit hex
        // code with full one.
        return;
      }

      const newValueAsHsv = convertToHsv(newValue);

      onChangeNonDerivedValueAsHsv(newValueAsHsv);
    },
    [onChangeNonDerivedValueAsHsv],
  );

  const onCopy = useCallback((event: ClipboardEvent<HTMLInputElement>) => {
    const inputElem = inputRef.current;
    if (!inputElem) {
      return;
    }

    const { selectionStart, selectionEnd, value } = inputElem;

    // If exactly 6 hex characters are selected (entire hex code without #)
    if (
      selectionStart === 1 &&
      selectionEnd === SIX_DIGIT_HEX_CODE_LENGTH &&
      value.length === SIX_DIGIT_HEX_CODE_LENGTH
    ) {
      event.preventDefault();
      const hexWithoutHash = value.slice(1);
      const hexWithHash = `#${hexWithoutHash}`;
      event.clipboardData.setData('text/plain', hexWithHash);
    }
  }, []);

  const onSelectCapture = useCallback(() => {
    const inputElem = inputRef.current;
    if (!inputElem) {
      return;
    }

    const { selectionStart, selectionEnd } = inputElem;

    // If selection includes the # character, adjust it to exclude the #
    if (selectionStart === 0 && selectionEnd != null && selectionEnd > 0) {
      inputElem.setSelectionRange(1, selectionEnd);
    }

    // If the selection is collapsed (zero-length) and at the start, move it one
    // character forward (i.e. from the left of the # to the right of the #)
    if (selectionStart === 0 && selectionEnd === 0) {
      inputElem.setSelectionRange(1, 1);
    }
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const { key, target, metaKey, ctrlKey } = event;
      const input = target as HTMLInputElement;
      const { selectionStart, selectionEnd, value } = input;

      // Allow all meta/ctrl key combinations (Cmd+A, Cmd+C, Cmd+V, etc.)
      if (metaKey || ctrlKey) {
        return;
      }

      // Handle arrow up/down for saturation nudging
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        const currentHsv = convertToHsv(valueAsHex);
        const saturationNudge = 0.02; // 2% adjustment
        const newSaturation = Math.max(
          0,
          Math.min(
            1,
            key === 'ArrowUp' ? currentHsv.s + saturationNudge : currentHsv.s - saturationNudge,
          ),
        );

        const newHsv = {
          ...nonDerivedValueAsHsv,
          s: newSaturation,
        };

        onChangeNonDerivedValueAsHsv(newHsv);
        return;
      }

      // Allow other navigation keys, backspace, delete, etc.
      if (key.length > 1) {
        return; // Allow navigation keys
      }

      // Only allow valid hex characters
      if (!isValidHexChar(key)) {
        event.preventDefault();
        return;
      }

      // Don't allow input beyond 6 hex digits (7 total chars including #)
      if (value.length >= SIX_DIGIT_HEX_CODE_LENGTH && selectionStart === selectionEnd) {
        event.preventDefault();
      }
    },
    [valueAsHex, onChangeNonDerivedValueAsHsv, nonDerivedValueAsHsv],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      const normalizedValue = normalizeHexInput(pastedText);
      const isValidHexValue = Boolean(parseHex(normalizedValue));

      setTextInputValue(normalizedValue);

      if (!isValidHexValue) {
        return;
      }

      const newValueAsHsv = convertToHsv(normalizedValue);
      onChangeNonDerivedValueAsHsv(newValueAsHsv);
    },
    [onChangeNonDerivedValueAsHsv],
  );

  const selectAllButTheHash = useCallback(() => {
    const inputElem = inputRef.current;
    if (!inputElem) {
      return;
    }

    inputElem.setSelectionRange(1, inputElem.value.length);
  }, []);

  const onFocusInput = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    selectAllButTheHash();
  }, [selectAllButTheHash]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }
    const inputElem = inputRef.current;
    if (!inputElem) {
      return;
    }

    inputElem.focus();
    selectAllButTheHash();
  }, [autoFocus, selectAllButTheHash]);

  // Sync text input with external color changes
  useEffect(() => {
    setTextInputValue(valueAsHex);
  }, [valueAsHex]);

  return (
    <Input
      ref={inputRef}
      aria-label="Hex color input"
      autoComplete="off"
      maxLength={SIX_DIGIT_HEX_CODE_LENGTH}
      monospace={true}
      onChange={onChangeInputValue}
      onCopy={onCopy}
      onFocus={onFocusInput}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onSelectCapture={onSelectCapture}
      spellCheck={false}
      type="text"
      value={textInputValue}
    />
  );
};

HexColorInput.displayName = 'HexColorInput';
